import { useEffect, useState, type ReactNode } from 'react';
import { formatEuro, parseAmountInput } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Caret, FieldFrame, TextField } from '../../../ds/components';
import { useFieldIds } from '../../../ds/hooks';
import styles from './create.module.css';

/** Form pieces of the create dialogs (A2 „Formularfeld"): locked value, select with a side tag, euro amount. */

export function ReadField({
  label,
  value,
  tag,
  hint,
  tone = 'locked',
}: {
  label: string;
  value: ReactNode;
  tag?: string | null | undefined;
  hint?: string | null | undefined;
  /** `locked` = sand-100 (Kontakt, Deal, Provision); `well` = card with the inset well (Betrag in 10). */
  tone?: 'locked' | 'well' | undefined;
}) {
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <span className={clsx(styles.readValue, tone === 'well' && styles.readWell)}>
        <span className={styles.readText}>{value}</span>
        {tag ? <span className={styles.tag}>{tag}</span> : null}
      </span>
      {hint ? <span className={styles.hint}>{hint}</span> : null}
    </div>
  );
}

export function TagSelect({
  label,
  value,
  options,
  tag,
  hint,
  error,
  onChange,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  tag?: string | null | undefined;
  hint?: string | null | undefined;
  error?: string | undefined;
  onChange: (value: string) => void;
}) {
  const ids = useFieldIds(undefined, { hint: Boolean(hint), error: Boolean(error) });
  return (
    <FieldFrame
      htmlFor={ids.inputId}
      label={label}
      hint={hint ?? undefined}
      error={error}
      hintId={ids.hintId}
      errorId={ids.errorId}
      trailing={
        <span className={styles.rateTrail}>
          {tag ? <span className={styles.tag}>{tag}</span> : null}
          <Caret size={11} />
        </span>
      }
    >
      <select
        id={ids.inputId}
        className={styles.select}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldFrame>
  );
}

/** Euro amount typed as „403.000" and shown as „403.000 €" after leaving the field; the tag sits on the right. */
export function AmountField({
  label,
  cents,
  tag,
  hint,
  error,
  autoFocus,
  onChange,
}: {
  label: string;
  cents: number | null;
  autoFocus?: boolean | undefined;
  tag?: string | null | undefined;
  hint?: string | null | undefined;
  error?: string | undefined;
  onChange: (cents: number | null) => void;
}) {
  const [text, setText] = useState(cents === null ? '' : formatEuro(cents));
  useEffect(() => {
    setText((current) =>
      parseAmountInput(current) === cents ? current : cents === null ? '' : formatEuro(cents),
    );
  }, [cents]);
  return (
    <TextField
      label={label}
      inputMode="decimal"
      autoFocus={autoFocus}
      value={text}
      hint={hint ?? undefined}
      error={error}
      trailing={tag ? <span className={styles.tag}>{tag}</span> : undefined}
      onChange={(event) => {
        setText(event.target.value);
        onChange(parseAmountInput(event.target.value));
      }}
      onBlur={() => {
        const parsed = parseAmountInput(text);
        if (parsed !== null) setText(formatEuro(parsed));
      }}
    />
  );
}

/** „0,80" → 0.8; empty or unreadable → null (at most three decimals, `numeric(6,3)`). */
export function parseRate(text: string): number | null {
  const normalized = text.replace(/[\s%\u00a0]/g, '').replace(',', '.');
  if (!/^\d{1,3}(\.\d{1,3})?$/.test(normalized)) return null;
  const value = Number(normalized);
  return value >= 0 && value <= 100 ? value : null;
}
